import { ChevronLeft, ChevronRight, CircleAlert, History } from "lucide-react";
import { MirrorTag } from "./mirror-mark";
import React, {
  useEffect,
  useRef,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import { Button, cn, EmptyState, Skeleton } from "@ndd/ui";
import type {
  AppointmentListItem,
  AppointmentStatus,
} from "../../../../shared/api-types";
import {
  appointmentWeekSearch,
  type AppointmentListSearch,
} from "../../lib/appointments";
import {
  earliestShownText,
  PAST_EMPTY_TITLE,
  PAST_END_LINE_LABEL,
  PAST_FIRST_LOAD_ERROR_TEXT,
  PAST_FIRST_LOAD_TEXT,
  pagerAriaLabel,
  pastEmptyDescription,
  pastWeekEmptyText,
  pastWeekErrorText,
  pastWeekHeading,
  pastWeekLoadingText,
  pastWeekMeta,
  pastWeekPager,
  pastWeekRows,
  type PagerDirection,
  type PastWeekClock,
  type PastWeekRow,
} from "../../lib/appointment-past-week";
import type {
  AppointmentPastWeek,
  PastWeekShown,
} from "../../lib/use-appointment-past-week";
import { Eyebrow } from "../v2/eyebrow";
import {
  AGENDA_CARD,
  AGENDA_SKELETON_CARD,
  AGENDA_TIME,
  AGENDA_UTILITY_RADIUS,
  agendaRowClass,
} from "./agenda-look";
import { AppointmentStatusToggle } from "./appointment-schedule";
import {
  agendaNameStruck,
  agendaStatusTone,
  AppointmentStatusMark,
  DraftReadyMark,
  useAgendaStatusSplit,
  type AppointmentStatusTone,
} from "./appointment-status";

/** What the page hands a Past row: the router Link lives outside this file. */
export interface PastWeekRowLinkProps {
  appointmentId: string;
  search: AppointmentListSearch;
  className: string;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}

export type PastWeekPanelState = Exclude<
  AppointmentPastWeek,
  { status: "idle" | "no_selection" | "unavailable" }
>;

export interface AppointmentPastWeekProps {
  state: PastWeekPanelState;
  /** Another RD's schedule: copy must not address the viewer as its owner. */
  viewingOther: boolean;
  listSearch: AppointmentListSearch;
  showNonDefaultStatuses: boolean;
  onStatusChange: (checked: boolean) => void;
  onWeekChange: (week: string) => void;
  onRowActivate: (appointmentId: string, event: MouseEvent<HTMLAnchorElement>) => void;
  renderLink: (props: PastWeekRowLinkProps) => ReactElement;
  headingRef?: RefObject<HTMLHeadingElement | null>;
  sectionRef?: RefObject<HTMLElement | null>;
}

const CARD = AGENDA_CARD;
const TOGGLE_LINE = "mt-2 flex justify-end @max-[560px]:justify-start";

/** The Past view (NO-767): one week of ended Appointments behind a week pager. */
export function AppointmentPastWeek(props: AppointmentPastWeekProps): ReactElement {
  const { state } = props;
  const toggle = (
    <AppointmentStatusToggle
      checked={props.showNonDefaultStatuses}
      onChange={props.onStatusChange}
    />
  );

  if (state.status === "loading") return <PastWeekSkeleton toggle={toggle} />;

  if (state.status === "first-error") {
    return <PastFirstLoadError toggle={toggle} retry={state.retry} />;
  }

  if (state.status === "empty") {
    return (
      <>
        <div className="mt-8 flex justify-end @max-[560px]:justify-start">{toggle}</div>
        <div className="mt-3">
          <EmptyState
            icon={History}
            title={PAST_EMPTY_TITLE}
            description={pastEmptyDescription(props.viewingOther)}
          />
        </div>
      </>
    );
  }

  return <PastWeekSection {...props} state={state} toggle={toggle} />;
}

function PastWeekSection(
  props: AppointmentPastWeekProps & { state: PastWeekShown; toggle: ReactNode }
): ReactElement {
  const { state, toggle } = props;
  const clock = state.display;
  const loading = state.phase === "loading";
  const error = state.phase === "error";
  const headingId = "appointments-past-week-heading";
  const { pagerRef, retryRef } = useRetryFocusOnError(error);

  return (
    <section ref={props.sectionRef} aria-labelledby={headingId} className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <h2
            id={headingId}
            ref={props.headingRef}
            tabIndex={-1}
            className="rounded font-sans text-base font-bold text-foreground outline-none focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-ring"
          >
            {pastWeekHeading(state.week, clock)}
          </h2>
          <span className="text-body text-muted-foreground">
            {pastWeekMeta(state.week, state.appointments.length, clock, { error })}
          </span>
        </div>
        <PastWeekPagerGroup
          state={state}
          pagerRef={pagerRef}
          onWeekChange={props.onWeekChange}
        />
      </div>
      <div className={TOGGLE_LINE}>{toggle}</div>
      <div
        className={cn(CARD, loading && "opacity-50 transition-opacity")}
        inert={loading}
      >
        <PastWeekBody {...props} retryRef={retryRef} />
      </div>
      {!state.hasEarlier && !error ? (
        <PastWeekEndLine
          appointments={state.appointments}
          timezone={clock.timezone}
          viewingOther={props.viewingOther}
        />
      ) : null}
    </section>
  );
}

/** On error, focus moves to Retry only if a pager button still had it. */
function useRetryFocusOnError(error: boolean) {
  const pagerRef = useRef<HTMLDivElement | null>(null);
  const retryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!error) return;

    const pager = pagerRef.current;
    if (pager && pager.contains(document.activeElement)) retryRef.current?.focus();
  }, [error]);

  return { pagerRef, retryRef };
}

function PastWeekPagerGroup({
  state,
  pagerRef,
  onWeekChange,
}: {
  state: PastWeekShown;
  pagerRef: RefObject<HTMLDivElement | null>;
  onWeekChange: (week: string) => void;
}): ReactElement {
  const loading = state.phase === "loading";
  const pager = pastWeekPager(state.week, state);

  // The buttons are never re-created, so focus stays on the pressed one while
  // its label and state update in place.
  return (
    <div
      ref={pagerRef}
      role="group"
      aria-label="Change week"
      className="flex shrink-0 items-center gap-2 @max-[560px]:mt-3 @max-[560px]:w-full @max-[560px]:flex-wrap"
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="mr-1 whitespace-nowrap text-body text-muted-foreground @max-[560px]:order-2 @max-[560px]:m-0 @max-[560px]:mt-0.5 @max-[560px]:w-full"
        >
          {pastWeekLoadingText(state.target, state.display)}
        </span>
      ) : null}
      <PagerButton
        direction="earlier"
        target={pager.earlier}
        loading={loading}
        clock={state.display}
        onWeekChange={onWeekChange}
      />
      <PagerButton
        direction="later"
        target={pager.later}
        loading={loading}
        clock={state.display}
        onWeekChange={onWeekChange}
      />
    </div>
  );
}

function PagerButton({
  direction,
  target,
  loading,
  clock,
  onWeekChange,
}: {
  direction: PagerDirection;
  target: string | null;
  loading: boolean;
  clock: PastWeekClock;
  onWeekChange: (week: string) => void;
}): ReactElement {
  const atEnd = target === null;
  const unavailable = atEnd || loading;
  const label = direction === "earlier" ? "Earlier week" : "Later week";

  return (
    <Button
      type="button"
      variant="utility"
      size="compact"
      shape="pill"
      aria-label={pagerAriaLabel(direction, target, clock)}
      aria-disabled={unavailable ? true : undefined}
      data-end={atEnd ? "true" : undefined}
      className={cn(
        "gap-1.5 px-3.5 text-body text-muted-strong @max-[560px]:h-11 @max-[560px]:flex-1",
        AGENDA_UTILITY_RADIUS,
        unavailable &&
          "cursor-default opacity-50 hover:bg-(--button-utility-bg,var(--background)) hover:text-muted-strong"
      )}
      onClick={() => {
        if (unavailable || target === null) return;

        onWeekChange(target);
      }}
    >
      {direction === "earlier" ? <ChevronLeft aria-hidden="true" /> : null}
      {label}
      {direction === "later" ? <ChevronRight aria-hidden="true" /> : null}
    </Button>
  );
}

function PastWeekBody(
  props: AppointmentPastWeekProps & {
    state: PastWeekShown;
    retryRef: RefObject<HTMLButtonElement | null>;
  }
): ReactElement {
  const { state } = props;

  if (state.phase === "error") {
    return (
      <PastCardError
        text={pastWeekErrorText(state.week, state.display)}
        retry={state.retry}
        retryRef={props.retryRef}
      />
    );
  }

  if (state.appointments.length === 0) {
    return (
      <div className="px-6 py-10 text-center">
        <p className="text-sm text-muted-foreground">
          {pastWeekEmptyText(state.week, state.display)}
        </p>
      </div>
    );
  }

  const search = appointmentWeekSearch(props.listSearch, state.week);

  return (
    <>
      {pastWeekRows(state.appointments, state.display.timezone).map((row) => (
        <PastWeekRowLink key={row.item.id} row={row} search={search} {...props} />
      ))}
    </>
  );
}

function PastWeekRowLink({
  row,
  search,
  renderLink,
  onRowActivate,
}: {
  row: PastWeekRow;
  search: AppointmentListSearch;
  renderLink: AppointmentPastWeekProps["renderLink"];
  onRowActivate: AppointmentPastWeekProps["onRowActivate"];
}): ReactElement {
  const { item } = row;
  const split = useAgendaStatusSplit();
  const struck = agendaNameStruck(item.status, split);

  return renderLink({
    appointmentId: item.id,
    search,
    onClick: (event) => onRowActivate(item.id, event),
    className: cn(
      agendaRowClass(false),
      row.startsDay && "shadow-[inset_0_1px_0_var(--color-sand-200)]"
    ),
    children: (
      <>
        <span
          aria-hidden="true"
          className={cn(
            "flex shrink-0 gap-2 whitespace-nowrap text-body text-muted-foreground transition-colors group-hover:text-muted-strong",
            AGENDA_TIME,
            // AGENDA_TIME sets the theme's ink; keep the classic hover
            // darkening under the theme too (ADR-0024's brand:/state pairing).
            "brand:group-hover:text-muted-strong"
          )}
        >
          <span className="w-12">{row.dayLabel ?? ""}</span>
          <span className="w-[62px]">{row.timeLabel}</span>
        </span>
        <span className="sr-only">{`${row.srWhen}, `}</span>
        <span className="flex min-w-0 flex-1 items-center gap-3.5 @max-[520px]:flex-col @max-[520px]:items-start @max-[520px]:gap-1">
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-sm font-medium text-foreground @max-[520px]:w-full",
              struck && "line-through"
            )}
          >
            {item.patientName || "Patient"}
          </span>
          <MirrorTag mirrorState={item.mirrorState} />
          <DraftReadyMark item={item} />
          <PastRowStatusPill item={item} />
        </span>
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-sand-300 transition-colors group-hover:text-muted-foreground"
        />
      </>
    ),
  });
}

// The Upcoming row mapping (appointment-status.tsx), restated here because
// every Past row already happened: Occurred and Scheduled carry no pill, and
// Rescheduled keeps readable contrast (muted-strong rather than
// muted-foreground). The mark itself is shared, so under the always theme a
// routine status is quiet text and only an exception is coloured (NO-796),
// and with rd-schedule on each missed status takes its own status pair
// through agendaStatusTone (NO-869); the split Rescheduled already carries
// muted-strong ink.
const PAST_BADGE_VARIANTS = {
  scheduled: null,
  checked_in: "info",
  occurred: null,
  no_show: "warning",
  cancelled: "warning",
  late_cancellation: "warning",
  rescheduled: "muted",
  unknown: "neutral",
} satisfies Record<AppointmentStatus, AppointmentStatusTone | null>;

function PastRowStatusPill({
  item,
}: {
  item: AppointmentListItem;
}): ReactElement | null {
  const split = useAgendaStatusSplit();
  const tone = agendaStatusTone(item.status, PAST_BADGE_VARIANTS[item.status], split);

  if (!tone) return null;

  const isQuietRescheduled = item.status === "rescheduled" && tone === "muted";

  return (
    <AppointmentStatusMark
      tone={tone}
      className={isQuietRescheduled ? "text-muted-strong" : undefined}
    >
      {item.statusLabel}
    </AppointmentStatusMark>
  );
}

function PastWeekEndLine({
  appointments,
  timezone,
  viewingOther,
}: {
  appointments: AppointmentListItem[];
  timezone: string;
  viewingOther: boolean;
}): ReactElement {
  const sentence = earliestShownText(appointments, timezone, viewingOther);

  return (
    <div className="mt-7 text-center">
      <div className="flex items-center gap-3 before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">
        <Eyebrow className="leading-4 text-muted-foreground">
          {PAST_END_LINE_LABEL}
        </Eyebrow>
      </div>
      {sentence ? (
        <p className="mt-1.5 text-body text-muted-foreground">{sentence}</p>
      ) : null}
    </div>
  );
}

function PastCardError({
  text,
  retry,
  retryRef,
}: {
  text: string;
  retry: () => void;
  retryRef?: RefObject<HTMLButtonElement | null>;
}): ReactElement {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
      <p className="flex items-center gap-1.5 text-body text-muted-strong">
        <CircleAlert aria-hidden="true" className="size-4 text-danger" />
        {text}
      </p>
      <Button
        ref={retryRef}
        type="button"
        variant="utility"
        size="compact"
        shape="pill"
        className={cn("px-5 text-body text-muted-strong", AGENDA_UTILITY_RADIUS)}
        onClick={retry}
      >
        Retry
      </Button>
    </div>
  );
}

/** F5b: nothing resolved yet, so there is no week to head or page from. */
function PastFirstLoadError({
  toggle,
  retry,
}: {
  toggle: ReactNode;
  retry: () => void;
}): ReactElement {
  return (
    <section aria-label="Past appointments" className="mt-8">
      <div className={TOGGLE_LINE}>{toggle}</div>
      <div className={CARD}>
        <PastCardError text={PAST_FIRST_LOAD_ERROR_TEXT} retry={retry} />
      </div>
    </section>
  );
}

const SKELETON_ROW_COUNT = 4;

function PastWeekSkeleton({ toggle }: { toggle: ReactNode }): ReactElement {
  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex flex-col gap-1">
          <Skeleton className="my-1 h-4 w-[88px]" />
          <Skeleton className="h-3 w-[150px]" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-32 rounded-full" />
          <Skeleton className="h-8 w-[116px] rounded-full" />
        </div>
      </div>
      <div className={TOGGLE_LINE}>{toggle}</div>
      <div
        role="status"
        aria-label={PAST_FIRST_LOAD_TEXT}
        className={cn("mt-3", AGENDA_SKELETON_CARD)}
      >
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
          <div
            key={index}
            className="flex items-center gap-4 border-b border-border px-5 py-4 last:border-b-0"
          >
            <Skeleton className="h-4 w-[110px]" />
            <Skeleton className="h-4 w-40" />
          </div>
        ))}
      </div>
    </section>
  );
}
